import { Dispatch, SetStateAction, useEffect, useState } from "react";
import FlexPassApi from "../../../../api/flexpassApi";
import CircleAvatar from "../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../components/Input/Input";
import StandartButton from "../../../../components/StandartButton/StandartButton";
import { FlexPassSubscription } from "../../../../types/FlexPassTypes";
import { SpecialistData } from "../../../../types/TypeSpecialist";
import { TypeUserData } from "../../../../types/TypeUsers";
import { getTranslation } from "../../../../utils/getTranslation";
import { SuccesNotify } from "../../../../utils/toaster";
import s from "./InviteSpecialists.module.css";

type InviteSpecialistsProps = {
  flexPassInfo: FlexPassSubscription;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
  setFlexPasses: Dispatch<SetStateAction<FlexPassSubscription[]>>;
  setSelectedFlexPass: Dispatch<
    SetStateAction<FlexPassSubscription | undefined>
  >;
};

const InviteSpecialists = ({
  flexPassInfo,
  setModalVisible,
  setFlexPasses,
  setSelectedFlexPass,
}: InviteSpecialistsProps) => {
  const token = localStorage.getItem("token");
  const [specialistData, setSpecialistData] = useState<
    (Omit<TypeUserData, "specialistIds"> & {
      specialistIds: SpecialistData;
      payedOutSessionsCount: number;
    })[]
  >([]);
  const [specialistToInvite, setSpecialistToInvite] = useState<
    (Omit<TypeUserData, "specialistIds"> & {
      specialistIds: SpecialistData;
      payedOutSessionsCount: number;
    })[]
  >([]);
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [errorInvites, setErrorInvites] =
    useState<{ userId: string; name: string; message: string }[]>();

  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    name?: string;
  }>({ page: 1, limit: 50, name: "" });

  useEffect(() => {
    loadSpecialists(true);
  }, [search]);
  useEffect(() => {
    if (filters.page === 1) return;
    loadSpecialists(false);
  }, [filters.page]);

  const loadSpecialists = async (reset = false) => {
    if (!token) return;

    setIsLoader(true);

    const specialistsResponse = await FlexPassApi.getSpecialistsFitForFlexPass(
      token,
      flexPassInfo._id,
      { ...filters, name: search }
    );

    if (specialistsResponse.status && specialistsResponse.specialists) {
      const newUsers = specialistsResponse.specialists.filter(
        (spec) => !specialistToInvite.some((s) => s._id === spec._id)
      );

      if (reset) {
        setSpecialistData(newUsers);
      } else {
        setSpecialistData((prev) => [...prev, ...newUsers]);
      }

      setHasMore(newUsers.length >= filters.limit!); // Если меньше лимита, значит больше нет
    } else {
      if (reset) setSpecialistData([]);
      setHasMore(false);
    }

    setIsLoader(false);
  };

  const inviteSpecialistsToFlexPass = async () => {
    if (!token) return;

    const specialistIds = specialistToInvite
      .filter((el) => el.specialistIds)
      .map((item) => item.specialistIds._id);
    const response = await FlexPassApi.inviteSpecToFlexPassSubscription(
      token,
      flexPassInfo._id,
      specialistIds
    );
    if (response.subscription && response.subscription.entryCodes) {
      setSelectedFlexPass((prev) =>
        prev ? { ...prev, entryCodes: response.subscription.entryCodes } : prev
      );
      setFlexPasses((prev) =>
        prev.map((item) => {
          if (item._id === flexPassInfo._id) {
            return { ...item, entryCodes: response.subscription.entryCodes };
          }
          return item;
        })
      );
    }

    if (response.failedInvites?.length) {
      setErrorInvites(
        response.failedInvites.map((item) => ({
          userId: item.user!._id ?? "",
          name:
            getTranslation(item.user!.name, flexPassInfo.language) +
            " " +
            getTranslation(item.user!.surname, flexPassInfo.language),
          message: item.message ?? "",
        }))
      );
    }
    if (response.status && !response.failedInvites?.length) {
      SuccesNotify("Invitations have been sent");
      setModalVisible(false);
    }
  };

  const addSpecialist = (
    spec: Omit<TypeUserData, "specialistIds"> & {
      specialistIds: SpecialistData;
      payedOutSessionsCount: number;
    }
  ) => {
    if (!specialistToInvite.find((s) => s._id === spec._id)) {
      setSpecialistToInvite([...specialistToInvite, spec]);
      setSpecialistData(specialistData.filter((s) => s._id !== spec._id)); // Убираем из списка
    }
  };

  const removeSpecialist = (id: string) => {
    const removed = specialistToInvite.find((s) => s._id === id);
    if (removed) {
      setSpecialistToInvite(specialistToInvite.filter((s) => s._id !== id));
      setSpecialistData([removed, ...specialistData]); // Возвращаем обратно
    }
  };

  if (errorInvites?.length)
    return (
      <div className={s.container} style={{ alignItems: "center" }}>
        <h2 style={{ textAlign: "center" }}>Failed Invites</h2>
        <div className={s.tableBlock}>
          <div className={s.rowTable}>
            <span>Expert</span>
            <span>Reason</span>
          </div>
          {errorInvites.map((item, index) => (
            <div key={index} className={s.rowTable}>
              <span style={{ display: "flex", alignItems: "center" }}>
                <CircleAvatar userId={item.userId} />
                {item.name}
              </span>
              <span>{item.message}</span>
            </div>
          ))}
        </div>
        <StandartButton
          action={() => setModalVisible(false)}
          buttonTitle="Close"
          width="200px"
        />
      </div>
    );

  return (
    <div className={s.container}>
      <h2 className={s.headTitle}>Invite Specialists</h2>
      <span>
        In the list of suggested experts, you will only see those who are
        eligible for the current FlexPass by subject, language, and specialty.
      </span>
      <Input
        inputValue={search}
        onChangeInput={(value) => {
          setSearch(value);
        }}
        placeholder="Search by name or email..."
        width="300px"
      />

      {/* Selected specialists */}
      {specialistToInvite.length > 0 && (
        <div className={s.selectedBlock}>
          <h3>Selected Specialists</h3>

          <div className={s.selectedList}>
            {specialistToInvite.map((spec) => (
              <div key={spec._id} className={s.selectedItem}>
                <CircleAvatar userId={spec._id} />
                <span>
                  {getTranslation(spec.name, flexPassInfo.language)}{" "}
                  {getTranslation(spec.surname, flexPassInfo.language)}
                </span>
                <button onClick={() => removeSpecialist(spec._id)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specialists list */}
      {isLoader && specialistData.length === 0 ? (
        <p>Loading...</p>
      ) : specialistData.length ? (
        <div className={s.listBlock}>
          {specialistData.map((spec) => (
            <div key={spec._id} className={s.listItem}>
              <CircleAvatar userId={spec._id} />
              <div className={s.info}>
                <div className={s.nameBlock}>
                  <span>
                    {getTranslation(spec.name, flexPassInfo.language)}{" "}
                    {getTranslation(spec.surname, flexPassInfo.language)}
                  </span>
                  <span>{`Completed ${spec.payedOutSessionsCount} sessions`}</span>
                </div>
                <span>{spec.email}</span>
              </div>

              <button
                className={s.addButton}
                onClick={() => addSpecialist(spec)}
              >
                Add
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p>No specialists found</p>
      )}

      {/* Show more button */}
      {hasMore && !isLoader && (
        <button
          className={s.showMore}
          onClick={() =>
            setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
          }
        >
          Show more
        </button>
      )}

      <div className={s.buttons}>
        <StandartButton
          action={inviteSpecialistsToFlexPass}
          buttonTitle="Send Invitations"
          width="200px"
          disabled={!specialistToInvite.length}
        />
        <StandartButton
          action={() => setModalVisible(false)}
          buttonTitle="Cancel"
          width="200px"
        />
      </div>
    </div>
  );
};

export default InviteSpecialists;
